import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";
import { FetchApi, FetchApiNoParam } from "../../../utils/api";

// Add this constant at the top
const CART_STORAGE_KEY = "product_cart";

// Standardized interfaces
interface Category {
  category_id: string;
  category_name: string;
}

interface VariationOption {
  id: string;
  title: string;
  price: string;
}

interface Variation {
  id: string;
  title: string;
  total_required: string;
  selection_type: string;
  options: VariationOption[];
}

// Product from API
interface Product {
  store_name: string;
  product_id: string;
  name: string;
  desc: string;
  image_url: string | null;
  price: string;
  app_promotion_price: string;
  status: string;
  store_id: string;
  type: string;
  monday_available: string;
  tuesday_available: string;
  wednesday_available: string;
  thursday_available: string;
  friday_available: string;
  saturday_available: string;
  sunday_available: string;
  collection_time_start: string | null;
  collection_time_end: string | null;
  order_daterange_start: string | null;
  order_daterange_end: string | null;
  collect_daterange_start: string | null;
  collect_daterange_end: string | null;
  collection_monday: string;
  collection_tuesday: string;
  collection_wednesday: string;
  collection_thursday: string;
  collection_friday: string;
  collection_saturday: string;
  collection_sunday: string;
  category: Category[];
  variations: Variation[];
}

// Simplified Cart structure - no nested variants
interface CartProduct {
  product_name: string;
  product_id: string;
  price_id: string;
  title: string;
  amount: string;
  pricePerItem?: string;
  quantity: number;
  variations: CartVariation[];
  pickupDate: string;
  pickupTime: string;
}

interface CartVariation {
  variationId: string;
  variationTitle: string;
  optionId: string;
  optionTitle: string;
  optionPrice: number;
}

interface CartStore {
  tempId: string;
  store_name: string;
  store_id: string;
  products: CartProduct[]; // Direct product details, no nested variants
}

type ProductCart = CartStore[];

// API request interfaces
interface ProductDetail {
  product_id: string;
  price: string;
  name: string;
  quantity: string;
  variants: string[];
  pickupdate: string;
  pickuptime: string;
}

interface BookingParams {
  store_id: number;
  name: string;
  phone: string;
  email: string;
  product_detail: ProductDetail[];
}

interface CurrentBooking {
  bookingDetails?: CartStore;
  bookingParams?: BookingParams;
}

// Add to cart input
interface NewProductCartItem {
  product_name: string;
  product_id: string;
  store_name: string;
  store_id: string;
  variant: Omit<CartProduct, "product_name" | "product_id">; // All CartProduct fields except the product identifiers
}

interface ProductBooking {
  order_id: string;
  order_date: string;
  firstName: string;
  lastname: string;
  email: string;
  phone: string;
  order_type: string;
  payment_status: string;
  subTotal: string;
  Total_amount: string;
  remark: string;
  order_status: string;
  order_from: string;
  product_detail: ProductDetailItem[];
}

interface ProductDetailItem {
  pickupdate: string;
  pickuptime: string;
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  prices: {
    price: string;
    promo: string;
  };
  stock_qty: string | null;
  params: {
    product_variant: ProductVariant[];
    qty: string;
    remark: string;
    store_id: string | null;
    total_price: number;
  };
}

interface ProductVariant {
  id: string;
  title: string;
  price: string;
  variation_id: string;
  variation_title: string;
}

// Context type
interface ProductContextType {
  generateTempId: () => string;
  productCarts: ProductCart;
  products: Product[];
  productBookings: ProductBooking[];
  productDetails: Product | null;
  loading: boolean;
  error: string | null;
  fetchProducts: (parameters: {
    store_id?: number;
    search?: string;
  }) => Promise<Product[]>;
  fetchProductDetails: (product_id?: number) => Promise<Product>;
  createProductBooking: (params: BookingParams) => Promise<any>;
  currentBooking: CurrentBooking;
  onCurrentBooking: (booking: CurrentBooking) => void;
  addToCart: (item: NewProductCartItem) => void;
  removeFromCart: (
    storeTempId: string,
    productPriceId?: string,
    pickupDate?: string,
    pickupTime?: string
  ) => void;
  productClearItem: (storeTempId: string, priceId: string) => void;
  fetchTempProductBooking: (tempBookingId: string) => void;
  clearCart: () => void;
  fetchProductBookings: (parameters: {
    UUID: string;
  }) => Promise<ProductBooking[]>;
  singleProductBooking: ProductBooking | null;
  fetchSingleProductBooking: (parameters: {
    order_id?: string;
    UUID: string;
  }) => Promise<ProductBooking>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider = ({ children }: { children: ReactNode }) => {
  const [productCarts, setProductCarts] = useState<ProductCart>(() => {
    if (typeof window !== "undefined") {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        try {
          return JSON.parse(savedCart);
        } catch (error) {
          console.error("Error parsing saved cart:", error);
          return [];
        }
      }
    }
    return [];
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [productBookings, setProductBookings] = useState<ProductBooking[]>([]);
  const [singleProductBooking, setSingleProductBooking] =
    useState<ProductBooking | null>(null);
  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const [currentBooking, setCurrentBooking] = useState<CurrentBooking>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [bookingError, setBookingError] = useState<any>(null);

  // Save to localStorage whenever productCarts changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(productCarts));
    }
  }, [productCarts]);

  const generateTempId = () => {
    return Math.random().toString(36).substring(2, 10);
  };

  // Updated fetchTempProductBooking for new structure
  const fetchTempProductBooking = (tempBookingId: string) => {
    const booking = productCarts.find(
      (store) => store.tempId === tempBookingId
    );

    if (!booking) {
      console.warn(`Booking with tempId ${tempBookingId} not found in cart`);
      return;
    }

    console.log("check booking ===> ", booking);

    // Create product_detail array from cart data
    const productDetail: ProductDetail[] = booking.products.map((product) => {
      // console.log("check product.variations ====> ", product.variations);
      // Extract option IDs from variations - use optionTitle since optionId might not exist
      // const variantOptionIds = product.variations.map(
      // (variation) => variation.optionId || ""
      // );

      return {
        product_id: product.product_id,
        price: parseFloat(`${product.pricePerItem}`).toFixed(2),
        name: product.product_name,
        quantity: product.quantity.toString(),
        variants: product.variations.map((variation) => variation.optionId),
        pickupdate: product.pickupDate,
        pickuptime: product.pickupTime,
      };
    });

    const _currentBooking: CurrentBooking = {
      bookingDetails: booking,
      bookingParams: {
        store_id: Number(booking.store_id),
        name: "",
        email: "",
        phone: "",
        product_detail: productDetail,
      },
    };

    console.log("check _currentBooking ===> ", _currentBooking);
    setCurrentBooking(_currentBooking);
  };

  const onCurrentBooking = (booking: CurrentBooking) =>
    setCurrentBooking(booking);

  const createProductBooking = useCallback(
    async (parameters: BookingParams): Promise<any> => {
      setBookingLoading(true);
      try {
        const url = FetchApiNoParam("create_product_booking.php");
        console.log("check url createProductBooking ===> ", url);

        const formData = new FormData();
        formData.append("store_id", parameters.store_id.toString());
        formData.append("name", parameters.name);
        formData.append("email", parameters.email);
        formData.append("phone", parameters.phone);

        if (parameters.product_detail && parameters.product_detail.length > 0) {
          parameters.product_detail.forEach((product, index) => {
            formData.append(
              `product_detail[${index}][product_id]`,
              product.product_id
            );
            formData.append(`product_detail[${index}][price]`, product.price);
            formData.append(`product_detail[${index}][name]`, product.name);
            formData.append(
              `product_detail[${index}][pickupdate]`,
              product.pickupdate
            );
            formData.append(
              `product_detail[${index}][pickuptime]`,
              product.pickuptime
            );
            formData.append(
              `product_detail[${index}][quantity]`,
              product.quantity
            );

            if (product.variants && product.variants.length > 0) {
              product.variants.forEach((variant, variantIndex) => {
                formData.append(
                  `product_detail[${index}][variants][${variantIndex}]`,
                  variant
                );
              });
            }
          });
        }

        console.log("FormData contents:");
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("check response createProductBooking ===> ", response);

        if (response.data.status === 1) return response.data;
        throw response.data;
      } catch (err) {
        setBookingError(err);
        console.log("Check CreateProductBooking Error ===> ", err);
        throw err;
      } finally {
        setBookingLoading(false);
      }
    },
    []
  );

  const fetchProductBookings = async (parameters: {
    UUID: string;
  }): Promise<ProductBooking[]> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("list_product_order.php", parameters);
      console.log("URL ======= fetchProductBookings", url);
      const response = await axios.get(url);

      if (response.data) {
        console.log(
          "Valid Response ======= fetchProductBookings",
          response.data
        );

        // Ensure we always set an array, even if response is empty
        const bookings = Array.isArray(response.data) ? response.data : [];
        setProductBookings(bookings);
        return bookings;
      } else {
        console.log(
          "Invalid Response ======= fetchProductBookings",
          response.data
        );
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchProductBookings", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch bookings";
      setError(errorMsg);

      // Set empty array on error to prevent crashes
      setProductBookings([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleProductBooking = async (parameters: {
    order_id?: string;
    UUID: string;
  }): Promise<ProductBooking> => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("product_order_detail.php", parameters);
      console.log("URL ======= fetchSingleProductBooking", url);
      const response = await axios.get(url);

      if (response.data) {
        console.log(
          "Valid Response ======= fetchSingleProductBooking",
          response.data
        );
        setSingleProductBooking(response.data);
        return response.data;
      } else {
        console.log(
          "Invalid Response ======= fetchSingleProductBooking",
          response.data
        );
        const errorMsg = "Invalid response from server";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.log("Catch Error ======= fetchSingleProductBooking", err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch bookings";
      setError(errorMsg);

      // Set empty array on error to prevent crashes
      setSingleProductBooking(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Simplified addToCart function with new structure
  const addToCart = (newItem: NewProductCartItem) => {
    const updatedCart = [...productCarts];

    const existingStoreIndex = updatedCart.findIndex(
      (store) => store.store_id === newItem.store_id
    );

    console.log(
      "check ewItem.variant.variations ===> ",
      newItem.variant.variations
    );

    // Create the cart product from new item
    const cartProduct: CartProduct = {
      product_name: newItem.product_name,
      product_id: newItem.product_id,
      price_id: newItem.variant.price_id,
      title: newItem.variant.title,
      amount: newItem.variant.amount,
      pricePerItem: newItem.variant.pricePerItem,
      quantity: newItem.variant.quantity,
      variations: newItem.variant.variations,
      pickupDate: newItem.variant.pickupDate,
      pickupTime: newItem.variant.pickupTime,
    };

    console.log("check cartProduct ===> ", cartProduct);

    if (existingStoreIndex !== -1) {
      const existingStore = updatedCart[existingStoreIndex];

      // Check if exact same product with same variant and pickup exists
      const existingProductIndex = existingStore.products.findIndex(
        (product) =>
          product.product_id === cartProduct.product_id &&
          product.price_id === cartProduct.price_id &&
          JSON.stringify(product.variations) ===
            JSON.stringify(cartProduct.variations) &&
          product.pickupDate === cartProduct.pickupDate &&
          product.pickupTime === cartProduct.pickupTime
      );

      if (existingProductIndex !== -1) {
        // Update quantity of existing product
        existingStore.products[existingProductIndex].quantity +=
          cartProduct.quantity;
      } else {
        // Add as new product
        existingStore.products.push(cartProduct);
      }
    } else {
      // Create new store
      const newStore: CartStore = {
        tempId: generateTempId(),
        store_name: newItem.store_name,
        store_id: newItem.store_id,
        products: [cartProduct],
      };
      updatedCart.push(newStore);
    }

    setProductCarts(updatedCart);

    console.log("check updatedCart ===> ", updatedCart);
  };

  // Simplified remove functions
  const removeFromCart = (
    storeTempId: string,
    priceId?: string,
    pickupDate?: string,
    pickupTime?: string
  ) => {
    let updatedCart: ProductCart;

    if (!priceId) {
      // Remove entire store
      updatedCart = productCarts.filter(
        (store) => store.tempId !== storeTempId
      );
    } else {
      // Remove specific product by price_id AND pickup details
      updatedCart = productCarts
        .map((store) => {
          if (store.tempId !== storeTempId) return store;

          return {
            ...store,
            products: store.products.filter(
              (product) =>
                !(
                  product.price_id === priceId &&
                  product.pickupDate === pickupDate &&
                  product.pickupTime === pickupTime
                )
            ),
          };
        })
        .filter((store) => store.products.length > 0);
    }

    setProductCarts(updatedCart);
  };

  const productClearItem = (storeTempId: string, priceId: string) => {
    removeFromCart(storeTempId, priceId);
  };

  const clearCart = () => {
    setProductCarts([]);
  };

  // Other functions remain the same...
  const fetchProducts = async (parameters: {
    store_id?: number;
    search?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("product_search.php", parameters);
      console.log("check fetchProducts url ===> ", url);
      const response = await axios.get(url);
      console.log("check fetchProducts response ===> ", response);

      if (response.data) {
        const productsData = Array.isArray(response.data)
          ? response.data
          : [response.data];
        setProducts(productsData);
        return productsData;
      } else {
        setError("Invalid response");
        return Promise.reject(response.data);
      }
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductDetails = async (product_id?: number) => {
    setLoading(true);
    setError(null);
    try {
      const url = FetchApi("product_detail.php", { product_id: product_id });
      console.log("check productDetails url ===> ", url);
      const response = await axios.get(url);

      if (response.data) {
        setProductDetails(response.data);
        return response.data;
      } else {
        setError("Invalid response");
        return Promise.reject(response.data);
      }
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      return Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductContext.Provider
      value={{
        generateTempId,
        productCarts,
        products,
        productBookings,
        productDetails,
        fetchProductDetails,
        currentBooking,
        loading,
        error,
        fetchProducts,
        onCurrentBooking,
        createProductBooking,
        addToCart,
        removeFromCart,
        productClearItem,
        fetchTempProductBooking,
        clearCart,
        fetchProductBookings,
        fetchSingleProductBooking,
        singleProductBooking,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProduct must be used within ProductProvider");
  }
  return context;
};
